import { Box, Button } from '@mui/material';
import { useCallback } from 'react';
import { FormState } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
  EmployeeProposalDecision,
  EmployeeProposalFormData,
} from '~/scenes/RewardingModule/components/EmployeeProposalsDialog/components/EmployeeProposal/components/Forms/Common';

type PropsType = {
  formState: FormState<EmployeeProposalFormData>;
  isNewProposal?: boolean;
  onFormSubmit?: () => void;
  onFormSubmitWithDecision?: (
    decision?: EmployeeProposalDecision,
  ) => () => void;
  onFormReset: () => void;
  readOnlyMode?: boolean;
  submitDecisionAllowed?: (isFormDirty: boolean) => boolean;
};

export const ProposalDataFormButtons = (props: PropsType) => {
  const {
    onFormSubmit,
    onFormSubmitWithDecision,
    onFormReset,
    isNewProposal,
    formState,
    readOnlyMode,
    submitDecisionAllowed,
  } = props;
  const { t } = useTranslation();
  const { dirtyFields, isSubmitting } = formState;

  const fieldsHaveChanged = !!Object.keys(dirtyFields).length;

  const submitWithDecision = !!onFormSubmitWithDecision;
  const decisionAllowed = !!submitDecisionAllowed
    ? submitDecisionAllowed(fieldsHaveChanged)
    : false;

  const handleSubmit = useCallback(
    (decision?: EmployeeProposalDecision) => {
      if (!!onFormSubmitWithDecision) {
        onFormSubmitWithDecision(decision)();
      } else if (!!onFormSubmit) {
        onFormSubmit();
      }
    },
    [onFormSubmitWithDecision, onFormSubmit],
  );

  if (readOnlyMode) {
    return <></>;
  }

  const submissionDisabled = !fieldsHaveChanged || isSubmitting;
  const decisionDisabled = !decisionAllowed || isSubmitting;

  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
      <Button disabled={submissionDisabled} onClick={onFormReset}>
        {t(isNewProposal ? 'clear' : 'reset')}
      </Button>
      {submitWithDecision && !isNewProposal && (
        <Box>
          <Button
            onClick={() => handleSubmit(EmployeeProposalDecision.Approve)}
            color={'success'}
            disabled={decisionDisabled}
          >
            {t('approve')}
          </Button>
          <Button
            onClick={() => handleSubmit(EmployeeProposalDecision.Reject)}
            color={'error'}
            disabled={decisionDisabled}
          >
            {t('reject')}
          </Button>
        </Box>
      )}
      {((submitWithDecision && isNewProposal) || !submitWithDecision) && (
        <Button disabled={submissionDisabled} onClick={() => handleSubmit()}>
          {t(isNewProposal ? 'create' : 'update')}
        </Button>
      )}
    </Box>
  );
};
